import React, { useEffect } from 'react';
import { Stack, Divider, TextField, Grid2 } from '@mui/material';
import { useSnackbar } from 'notistack';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { httpsCallable } from 'firebase/functions';
import UserInformation from './user-information';
import Devices from './devices';
import firebaseApp from '../../../firebase-app';
import { query, collection, where, getDocs } from 'firebase/firestore';
import Subscriptions from './subscriptions';
import Products from './products';
import LoadingButton from '@mui/lab/LoadingButton';
import { useStringsContext } from '../../../contexts/strings';
import { FirebaseUser, PersonProduct } from './types';
import SceneHeader from '../../common/scene/scene-header';
import ScenePage from '../../common/scene/scene-page';

const UserComponent = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = React.useState(0);
  const [searchText, setSearchText] = React.useState('');
  const [startSearch, setStartSearch] = React.useState(false);
  const [user, setUser] = React.useState<FirebaseUser | null>(null);
  const [ceeProducts, setCeeProducts] = React.useState<PersonProduct[] | null>(null);
  const [loadingUser, setLoadingUser] = React.useState(false);
  const [loadingProducts, setLoadingProducts] = React.useState(false);
  const { strings } = useStringsContext();

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  useEffect(() => {
    const findUser = async () => {
      setLoadingUser(true);
      const userRef = collection(firebaseApp.db, 'user');
      const userQuery = query(userRef, where('email', '==', searchText));
      const data = await getDocs(userQuery);

      if (data && data.docs?.length > 0) {
        const foundUser = data.docs[0].data() as FirebaseUser;
        setUser(foundUser);
      } else {
        enqueueSnackbar(strings.messages.findUserError, {
          variant: 'error',
        });
      }
      setLoadingUser(false);
    };

    if (startSearch) {
      setUser(null);
      setStartSearch(false);
      findUser();
    }
  }, [enqueueSnackbar, searchText, startSearch, strings]);

  useEffect(() => {
    const getProducts = async () => {
      if (user) {
        setLoadingProducts(true);
        const response: any = await httpsCallable(
          firebaseApp.functions,
          'getPersonProducts'
        )({
          id: user.uid,
        });
        setCeeProducts(response.data);
        setLoadingProducts(false);
      }
    };

    getProducts();
  }, [user]);

  const loading = loadingUser || loadingProducts;

  return (
    <ScenePage title={strings.appBar.accountInfo} breadCrumps={[strings.appBar.superAdmin]}>
      <SceneHeader title={strings.appBar.accountInfo} border />
      <Grid2 sx={{ mt: 4 }} container direction="column" spacing={2}>
        <Grid2>
          <TextField
            fullWidth
            id="outlined-basic"
            label="Användarens email"
            variant="outlined"
            onChange={(e) => {
              setSearchText(e.target.value);
            }}
            style={{
              maxWidth: '400px',
            }}
          />
        </Grid2>
        <Grid2>
          <LoadingButton
            loading={loading}
            disabled={searchText?.length === 0}
            variant="contained"
            onClick={() => setStartSearch(true)}
            style={{
              width: '200px',
            }}
          >
            Sök
          </LoadingButton>
        </Grid2>
      </Grid2>
      {user && user?.uid && !loading && (
        <>
          <Divider sx={{ mt: 4, mb: 1 }} />
          <Stack spacing={2}>
            <Tabs value={tab} onChange={handleChangeTab}>
              <Tab label="Användare" />
              <Tab label="Abonnemang" />
              <Tab label="Produkter" />
              <Tab label="Enheter" />
            </Tabs>
            {tab === 0 && <UserInformation user={user} loading={loading} />}
            {tab === 1 && <Subscriptions ceeProducts={ceeProducts} user={user} loading={loading} />}
            {tab === 2 && <Products user={user} loading={loading} />}
            {tab === 3 && <Devices user={user} />}
          </Stack>
        </>
      )}
    </ScenePage>
  );
};

export default UserComponent;
